import { GetterTree } from 'vuex'

// Types
import { Document } from '@/services/DocumentsService.interface'
import { State, TypedActionTree, LoadingErrorState } from '../types'

// Service
import DocumentsService from '@/services/DocumentsService'

// Define actions for documents
export type DocumentsActions = 'getDocuments'

// Map of documents per bookings
interface DocumentMap {
  [bookingReference: string]: Document[]
}

// Vuex State for Documents
export interface DocumentsState extends LoadingErrorState<DocumentsActions> {
  documentMap: DocumentMap
}

// Initial State
const state: DocumentsState = {
  documentMap: {},
  loading: {},
  error: {},
}

// Mutation payloads
interface SetDocumentsPayload {
  documents: Document[]
  bookingReference: string
}

// Mutations
const mutations = {
  setDocuments(state: DocumentsState, payload: SetDocumentsPayload) {
    state.documentMap[payload.bookingReference] = payload.documents
  },
  setLoading(
    state: DocumentsState,
    { action, status }: { action: DocumentsActions; status: boolean }
  ) {
    state.loading[action] = status
  },
  setError(
    state: DocumentsState,
    { action, message }: { action: DocumentsActions; message: string | null }
  ) {
    state.error[action] = message
  },
}

// Action payload interface
interface GetDocumentsPayload {
  bookingReference: string
  boReference?: string
}

// Actions using TypedActionTree
const actions: TypedActionTree<DocumentsActions, DocumentsState, State> = {
  async getDocuments({ commit, state }, payload: GetDocumentsPayload | string) {
    const action = 'getDocuments'
    commit('setLoading', { action, status: true })
    commit('setError', { action, message: null })
    
    // Handle both old format (string) and new format (object with boReference)
    const bookingReference = typeof payload === 'string' ? payload : payload.bookingReference
    const boReference = typeof payload === 'string' ? undefined : payload.boReference
    
    // Clear existing documents for this booking to force fresh fetch (useful for mock mode in dev)
    const isDevelopment = process.env.NODE_ENV !== 'production'
    if (isDevelopment && (window as any).MOCK_DOCUMENTS === true) {
      console.log('🔧 MOCK MODE: Clearing cached documents for fresh fetch')
      delete state.documentMap[bookingReference]
    }
    
    try {
      const documentsService = new DocumentsService()
      const documents: Document[] =
        await documentsService.getActiveDocuments(bookingReference, boReference)
      commit('setDocuments', { bookingReference, documents })
    } catch (error) {
      console.error('Error fetching documents:', error)
      commit('setError', {
        action,
        message: `Failed to fetch documents for booking ${bookingReference}.`,
      })
    } finally {
      commit('setLoading', { action, status: false })
    }
  },
}

// Getters
const getters: GetterTree<DocumentsState, State> = {
  /**
   * Check if any action is loading
   */
  isLoadingAny: (state: DocumentsState) => {
    return Object.values(state.loading).some(status => status)
  },

  /**
   * Check if a specific action is loading
   */
  isLoading: (state: DocumentsState) => (action: DocumentsActions) => {
    return !!state.loading[action]
  },

  /**
   * Check if there are any errors
   */
  hasErrorAny: (state: DocumentsState) => {
    return Object.values(state.error).some(message => message !== null)
  },

  /**
   * Get the error message for a specific action
   */
  getError: (state: DocumentsState) => (action: DocumentsActions) => {
    return state.error[action] || null
  },

  /**
   * Get all documents
   */
  getDocuments: (state: DocumentsState) => {
    return state.documentMap
  },

  /**
   * Get documents for a specific booking reference
   */
  getDocumentsByBookingReference:
    (state: DocumentsState) => (bookingReference: string) => {
      return state.documentMap[bookingReference] || []
    },
}

// Export the Vuex module
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
